import { useContext, useEffect, useRef, useState } from "react";
import { Typography, Button, Row, Col, Card, Tooltip, Input } from "antd";
import { LoadingOutlined, RedoOutlined } from "@ant-design/icons";
import genAi from "../../assets/svg/gen-ai.svg";
import { AuthContext } from "../../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import {
  aiChatConversation,
  createAISession,
  resetChatConversation,
  resetChatSession,
  setLoading,
  userConversationMessage,
} from "../../actionCreator/genAIActionCreator";
import { useEmailWebSocket } from "../../common/hooks/useEmailWebSocket";
import { useAlert } from "../../context/AlertProvider";
import MarkdownRenderer from "../../common/MarkdownRenderer";
import RightPanel from "./RightPanel";
import { ReactComponent as DealSummary } from "../../assets/svg/icons/deal-summary.svg";
import { ReactComponent as Calendar } from "../../assets/svg/icons/calendar-ai.svg";
import { ReactComponent as UsageSummary } from "../../assets/svg/icons/usage-summary.svg";
import { ReactComponent as Events } from "../../assets/svg/icons/upcoming-events.svg";
import chatAi from "../../assets/svg/icons/chat-ai.svg";
import send from "../../assets/svg/icons/chat-send.svg";

const { Text } = Typography;

const starterCards = [
  {
    id: "GET_DEAL_SUMMARY",
    query: "Get deals converted in the last week.",
    title: "Get Deal Summary",
    icon: <DealSummary />,
  },
  {
    id: "GET_UPCOMING_EVENTS",
    query: "Get upcoming events in the next week.",
    title: "Get Upcoming Events",
    icon: <Calendar />,
  },
  {
    id: "GET_USAGE_SUMMARY",
    query: "Get me usage summary details.",
    title: "Get Usage Summary",
    icon: <UsageSummary />,
  },
  {
    id: "GET_LEAD_SUMMARY",
    query: "get me new leads from the last week.",
    title: "Get Lead Summary",
    icon: <Events />,
  },
];

export default function ChatInterface() {
  const [value, setValue] = useState("");
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [sessionId, setSessionId] = useState();
  const [showSidePanel, setShowSidePanel] = useState(false);
  const { subscribeToSession, unsubscribeFromSession, sendInputToSession } =
    useEmailWebSocket("chat-channel-1");

  const chatConversation = useSelector(
    (state) => state.genAIReducer.chatConversation,
  );
  const loading = useSelector((state) => state.genAIReducer.loading);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    resetSession();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [chatConversation]);

  const sendQuery = (query) => {
    dispatch(userConversationMessage(query));
    handleConversation(query);
  };

  const sendMessage = () => {
    if (!value.trim()) return;

    setValue("");
    dispatch(userConversationMessage(value));
    handleConversation(value);
  };

  const handleConversation = async (query) => {
    try {
      dispatch(setLoading(true));
      let currentSessionId = sessionId;
      if (!sessionId) {
        const res = await dispatch(createAISession());
        currentSessionId = res?.data?.sessionId;
        setSessionId(res?.data?.sessionId);
        await subscribeToSession(currentSessionId);
      }

      const payloadObj = {
        userId: user?.id,
        query: query,
        sessionId: currentSessionId,
        accountId: user?.account?.id,
      };

      await dispatch(aiChatConversation(payloadObj));
    } catch (err) {
      console.error(err);
      showAlert("error", "AI generation failed");
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const resetSession = async () => {
    await dispatch(resetChatSession(sessionId));
    await unsubscribeFromSession();
    dispatch(resetChatConversation());
    setSessionId();
  };

  return (
    <Row className="tw-w-full">
      <Col md={showSidePanel ? 8 : 24}>
        <Card
          title={
            <div className="tw-flex tw-items-center gap-20">
              <div className="user-profile text-uppercase">
                <img src={genAi} alt="" />
              </div>
              <div className="d-flex flex-column py-10">
                <Text className="heading18px fw-semibold">Leadzy</Text>
                {/* <Text className="heading13px text-color-888888">
                  Powered By OpenAI
                </Text> */}
              </div>
            </div>
          }
          className={`chat-widget p-0 ${showSidePanel ? "tw-rounded-[10px_0_0_10px]" : "tw-rounded-[10px]"}`}
        >
          {chatConversation.length === 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-mb-8">
              <img src={chatAi} alt="" />
              <div className="tw-text-2xl tw-font-medium tw-text-text-primary">
                Hi, How can I help you today?
              </div>
              <div className="tw-text-lg tw-font-medium tw-text-sub-text">
                Ask about guest inquiries, upcoming events, or engagement
                insights
              </div>
            </div>
          )}
          <div
            style={{
              flex: 1,
            }}
            className="tw-flex tw-flex-col tw-justify-between"
          >
            <div
              ref={messagesEndRef}
              className={`tw-flex tw-flex-col tw-items-center ${chatConversation.length && "tw-overflow-y-auto tw-h-[calc(100vh-285px)]"}`}
            >
              <div
                className={`${!showSidePanel && "tw-max-w-[80%]"} tw-w-full tw-px-2`}
              >
                {chatConversation.length === 0 ? (
                  <div className="tw-flex tw-justify-end tw-flex-col tw-h-full">
                    <Row className="m-0" gutter={[24, 24]}>
                      {starterCards.map((card) => (
                        <Col span={12} key={card.id}>
                          <div
                            onClick={() => sendQuery(card.query)}
                            className="chat-informative-card tw-group"
                          >
                            <div className="tw-flex tw-items-center tw-justify-center tw-border tw-size-[38px] tw-border-solid tw-border-[#CDD2DF] tw-rounded-[10px] group-hover:tw-bg-theme tw-text-theme group-hover:tw-text-white group-hover:tw-border-theme">
                              {card?.icon}
                            </div>
                            <div>
                              <div className="tw-text-sm tw-font-bold">
                                {card.title}
                              </div>
                              <div className="tw-text-sm tw-text-sub-text">
                                {card.query}
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  chatConversation
                    ?.filter((d) => d?.text !== "")
                    .map((msg, i) => (
                      <div
                        key={msg.id}
                        className={`d-flex gap-10 mb20 ${msg.from === "user" ? "justify-content-end" : "justify-content-start"}`}
                      >
                        {msg.from === "bot" && (
                          <div className="user-profile text-uppercase">
                            <img src={genAi} alt="" />
                          </div>
                        )}

                        <div
                          className={`chat-message ${msg.from === "user" ? "tw-bg-[#F1EFFF] tw-rounded-tl-[10px] tw-rounded-bl-[10px] tw-rounded-br-[10px] tw-rounded-tr-none" : "tw-bg-white tw-rounded-tl-none tw-rounded-bl-[10px] tw-rounded-br-[10px] tw-rounded-tr-[10px]"}`}
                        >
                          {loading &&
                          i ===
                            chatConversation?.filter((d) => d?.text !== "")
                              ?.length -
                              1 ? (
                            <LoadingOutlined />
                          ) : (
                            <MarkdownRenderer
                              content={msg.text}
                              sendQuery={(val) => sendQuery(val)}
                            />
                          )}
                        </div>

                        {msg.from === "user" && (
                          <div className="user-profile text-uppercase">
                            {user?.firstName?.[0]} {user?.lastName?.[0]}
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-items-center">
              <div
                className={`${!showSidePanel && "tw-max-w-[80%]"} tw-w-full`}
              >
                <div className="tw-mt-5 tw-w-full">
                  <div className="tw-flex tw-w-full tw-bg-white tw-items-center p5px py-10 tw-border tw-border-solid tw-border-[#CDD2DF] tw-rounded-[64px]">
                    <Input
                      bordered={false}
                      placeholder="Write your prompt here..."
                      value={value}
                      onPressEnter={sendMessage}
                      onChange={(e) => setValue(e.target.value)}
                      style={{
                        paddingLeft: 8,
                      }}
                    />
                    <div className="tw-flex tw-gap-2 tw-items-center">
                      <Button
                        type="primary"
                        className="tw-bg-theme tw-border-theme tw-text-white"
                        disabled={loading}
                        shape={showSidePanel ? "circle" : "round"}
                        onClick={sendMessage}
                      >
                        {!showSidePanel && "Send"}
                        {loading ? (
                          <LoadingOutlined />
                        ) : (
                          <img src={send} alt="" />
                        )}
                      </Button>
                      {!!chatConversation?.length && (
                        <Tooltip title="Reset chat">
                          <Button
                            shape="circle"
                            disabled={loading}
                            icon={
                              loading ? <LoadingOutlined /> : <RedoOutlined />
                            }
                            onClick={resetSession}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Col>
      <Col md={showSidePanel ? 16 : 0}>
        <RightPanel
          sessionId={sessionId}
          chatConversation={chatConversation}
          sendInputToSession={sendInputToSession}
          setShowSidePanel={setShowSidePanel}
          showSidePanel={showSidePanel}
          handleConversation={(query) => {
            dispatch(userConversationMessage(query));
          }}
          sendQuery={sendQuery}
        />
      </Col>
    </Row>
  );
}
